/**
 * Presentación de una traza de crash resuelta.
 *
 * Sin JSX para poder probarlo con el ejecutor de Node.
 *
 * La decisión de fondo: el panel muestra el **texto que arma el servidor**, no
 * una reconstrucción propia marco por marco. Son dos formatos distintos —un
 * volcado nativo y una traza de la JVM— y cada uno tiene sus marcas: `[del
 * volcado]` para lo que ya venía resuelto, `← o …` para los métodos que el
 * número de línea no alcanzó a desempatar. Rehacer ese formateo acá sería
 * mantener las mismas reglas en dos lugares, y el día que se separen el panel
 * y el texto que alguien pega en un ticket van a decir cosas distintas.
 *
 * Lo que sí se decide acá es el marco alrededor: qué tan confiable es lo que
 * se está mostrando y qué falta para que lo sea más.
 */

export type Emparejado = 'map_id' | 'version' | null;

export interface EstadoDeResolucion {
  /** Qué tan confiable es lo que se muestra. */
  nivel: 'resuelto' | 'parcial' | 'crudo';
  titulo: string;
  /** Qué habría que hacer para mejorarlo. Null si no hay nada que hacer. */
  quePuedoHacer: string | null;
}

export interface DatosCrash {
  tipo: string;
  aviso: string | null;
  emparejado?: Emparejado;
  /** Solo en crashes nativos. */
  faltantes?: Array<{ nombre: string; buildId: string }>;
  /** Solo en excepciones de la JVM. */
  resueltos?: number;
}

/**
 * Traduce el estado a algo accionable.
 *
 * Un volcado sin resolver y uno resuelto se ven parecidos en pantalla —una
 * lista de líneas monoespaciadas— y la diferencia importa muchísimo. Sin esto,
 * alguien mira direcciones hexadecimales y concluye que el crash no se puede
 * investigar, cuando lo que falta es subir un archivo.
 */
export function estadoDeResolucion(datos: DatosCrash): EstadoDeResolucion {
  const faltantes = datos.faltantes ?? [];

  if (!datos.aviso && faltantes.length === 0) {
    return { nivel: 'resuelto', titulo: 'Traza resuelta', quePuedoHacer: null };
  }

  const aviso = datos.aviso ?? '';

  // Antes que el caso general de `faltantes`, porque en iOS lo que falta no es
  // un `.so` sino un dSYM, y no está en el mismo lugar del pipeline. Decir
  // «sube los símbolos» mandaría a buscar en la carpeta equivocada.
  if (aviso.includes('dSYM')) {
    return {
      nivel: 'parcial',
      titulo: 'Falta el dSYM',
      quePuedoHacer:
        `Sube el dSYM de ${faltantes.map((f) => f.nombre).join(', ')}: está en el paquete ` +
        '`.dSYM` que deja Xcode, adentro de `Contents/Resources/DWARF/`. Tiene que ser el de ' +
        'esta compilación exacta — el UUID no coincide entre dos compilaciones de la misma ' +
        'versión.',
    };
  }

  if (aviso.includes('formato de MetricKit')) {
    return {
      nivel: 'crudo',
      titulo: 'La traza no se pudo interpretar',
      quePuedoHacer:
        'Llegó en un formato que no es el de MetricKit. Se muestra tal como llegó; ' +
        'avisale a quien mantiene el SDK.',
    };
  }

  if (faltantes.length > 0) {
    return {
      nivel: 'parcial',
      titulo: 'Faltan símbolos',
      quePuedoHacer: `Sube los símbolos de ${faltantes
        .map((f) => f.nombre)
        .join(', ')} desde el pipeline de release.`,
    };
  }

  if (aviso.includes('llvm-symbolizer')) {
    // No es culpa de quien mira ni de los archivos subidos: es el servidor.
    // Decirle que suba algo lo mandaría a perder el tiempo.
    return {
      nivel: 'crudo',
      titulo: 'El servidor no puede resolver esta traza',
      // Vale para las dos plataformas: `llvm-symbolizer` resuelve tanto los
      // `.so` de Android como los dSYM de iOS, y si falta, faltan las dos.
      quePuedoHacer: 'Avisale a quien administra el servidor: falta llvm-symbolizer.',
    };
  }

  if (aviso.includes('no hay mapping.txt cargado')) {
    return {
      nivel: 'crudo',
      titulo: 'Sin mapping.txt para esta versión',
      quePuedoHacer: 'Sube el mapping.txt de esta compilación desde el pipeline de release.',
    };
  }

  if (aviso.includes('otra compilación')) {
    return {
      nivel: 'crudo',
      titulo: 'El mapping.txt no corresponde a esta compilación',
      quePuedoHacer:
        'Se recompiló la misma versión y el mapping guardado es de otra. Configurá ' +
        'android.r8.sourceFileTemplate=%MAP_ID para que el emparejado sea exacto.',
    };
  }

  return { nivel: 'parcial', titulo: 'Resuelta en parte', quePuedoHacer: null };
}

/**
 * Cómo se eligió el archivo de símbolos, dicho para alguien que no conoce el
 * detalle. Cambia cuánto creerle a los nombres que está leyendo.
 */
export function describirEmparejado(emparejado: Emparejado): string | null {
  if (emparejado === 'map_id') return 'compilación exacta';
  if (emparejado === 'version') return 'por versión, puede no ser la misma compilación';
  return null;
}

/** Identificador corto, para no ocupar media línea con 32 caracteres. */
export function trazaCorta(traceId: string): string {
  return traceId.length > 12 ? `${traceId.slice(0, 12)}…` : traceId;
}
